import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { requestPasswordReset } from "@/features/identity/identity.api";
import { LogoLockup } from "@/shared/components/brand/logo-mark";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";

/**
 * `/forgot-password`. Collects an email and asks the shore to send a reset
 * link. The response is always "sent" (the shore never reveals whether an
 * account exists, to avoid email enumeration), so the success panel is shown
 * regardless once the request resolves.
 */
export function ForgotPasswordRoute() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useMutation({ mutationFn: requestPasswordReset });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email) return;
    try {
      await request.mutateAsync({ email });
      setSent(true);
    } catch {
      setError("Could not send the reset link. Try again.");
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-8 px-6 py-12">
      <div>
        <LogoLockup className="w-fit" />
        <h1 className="font-display mt-4 text-3xl font-semibold tracking-tighter">
          Reset your password
        </h1>
        <p className="mt-2 text-sm text-text-secondary">
          Enter your email and we'll send you a link to choose a new password.
        </p>
      </div>

      {sent ? (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-border bg-surface-subtle p-4 text-sm">
            <p className="font-medium">Check your email.</p>
            <p className="mt-1 text-text-secondary">
              If an account exists for <span className="font-medium">{email}</span>, we've sent a
              link to reset your password. It expires in 15 minutes.
            </p>
            <p className="mt-2 text-xs text-text-tertiary">
              Dev: open the shore terminal, the reset link is logged there.
            </p>
          </div>
          <Link
            to="/login"
            search={{}}
            className="text-sm text-text-brand underline-offset-4 hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button type="submit" disabled={request.isPending}>
            {request.isPending ? "Sending…" : "Send reset link"}
          </Button>
          <Link
            to="/login"
            search={{}}
            className="text-xs text-text-tertiary underline-offset-4 hover:text-text-secondary hover:underline"
          >
            Back to sign in
          </Link>
        </form>
      )}
    </div>
  );
}
